<script>
    export let productDetails = [];

    import MaterialRoundedWarning from "../presentational/icons/MaterialRoundedWarning.svelte";
    import Spinner from "../Spinner.svelte";
    import CustomRadio from "../CustomRadio.svelte";
    import Button from "../Button.svelte";
    import HorizontalRule from "../presentational/HorizontalRule.svelte";
    import View from "../presentational/View.svelte";
    import { slide } from "svelte/transition";

    const steps = [];
    let activeStepIdx = 0;
    const processors = {
        PAYPAL: "PayPal",
        STRIPE: "Stripe",
    };
    let selectedProcessor = processors.STRIPE;
    let selectedOption = "YEARLY";
    let selectedCurrency = "$";
    let yearlyPrice = "15";
    let monthlyPrice = "1.50";

    $: thisCurrency = selectedCurrency;
    $: isYearly = selectedOption === "YEARLY";
    $: isMonthly = selectedOption === "MONTHLY";
    $: selectedPlan = selectedOption === "YEARLY" ? "1-year plan" : "monthly plan";
    $: selectedPrice = selectedOption === "YEARLY" ? yearlyPrice : monthlyPrice;

    $: monthlyBtnText = thisCurrency + monthlyPrice + " per month";
    $: yearlyBtnText = thisCurrency + yearlyPrice + " per year";

    /*
        Cards (data-step=)
        0 Choose payment option and method.
        1 Payment in progress (Stripe only, most likely)
        2 Failure case (if defined)
        3 Success
    */
    const navigateToIndex = (newIdx) => {
        // move the display to the card with index newIdx (if newIdx is valid.)
        if (activeStepIdx === newIdx || !Number.isInteger(+newIdx) || newIdx < 0 || newIdx >= steps.length) return;
        // navigate and update active idx
        steps[activeStepIdx].classList.add("hidden");
        steps[newIdx].classList.remove("hidden");
        activeStepIdx = newIdx;
    };

    const switchCurrency = (ev) => {
        selectedCurrency = ev.target.value;
    };

    const completePurchase = () => {
        navigateToIndex(2);
    };
</script>

<View viewType="{'card-wrap'}">
    <div class="card-content" data-step="0" bind:this="{steps[0]}">
        <h3>Select Plan and Complete Purchase</h3>
        <HorizontalRule />
        <div class="row collapse-to-col justify-content-center">
            <div class="left row" transition:slide|local>
                <div class="row">
                    <h4>Payment Type</h4>
                    <div class="row collapse-to-col">
                        <div
                            class="half-row align-items-center"
                            on:click="{() => (selectedProcessor = processors.STRIPE)}"
                        >
                            <CustomRadio
                                on:click="{() => (selectedProcessor = processors.STRIPE)}"
                                isSelected="{selectedProcessor == processors.STRIPE}"
                            />
                            <img
                                src="/assets/CC_exportable@2x.png"
                                alt="Credit Card logos"
                                width="88px"
                                height="auto"
                            />
                        </div>
                        <div
                            class="half-row align-items-center"
                            on:click="{() => (selectedProcessor = processors.PAYPAL)}"
                        >
                            <CustomRadio
                                on:click="{() => (selectedProcessor = processors.PAYPAL)}"
                                isSelected="{selectedProcessor == processors.PAYPAL}"
                            />
                            <img src="/assets/paypal.svg" alt="PayPal logo" />
                        </div>
                    </div>
                </div>
                <div class="row collapse-to-col">
                    <h4>Plan Selection</h4>
                    <div class="row collapse-to-col">
                        <select id="pmt-currency" on:blur="{switchCurrency}" on:change="{switchCurrency}">
                            {#each productDetails.currencies as currency}
                                <option name="{currency.text}" value="{currency.symbol}">{currency.text}</option>
                            {/each}
                        </select>
                        <Button
                            buttonText="{monthlyBtnText}"
                            classNames="{'payment-option ' + (isMonthly ? 'selected' : '')}"
                            on:click="{() => (selectedOption = 'MONTHLY')}"
                        >
                            <div slot="leading" style="display:{isMonthly ? 'flex' : 'none'};">
                                <i class="material-icons">check</i>
                            </div>
                        </Button>
                        <Button
                            buttonText="{yearlyBtnText}"
                            classNames="{'payment-option ' + (isYearly ? 'selected' : '')}"
                            on:click="{() => (selectedOption = 'YEARLY')}"
                        >
                            <div slot="leading" style="display:{isYearly ? 'flex' : 'none'};">
                                <i class="material-icons">check</i>
                            </div>
                        </Button>
                    </div>
                </div>
            </div>
            {#if selectedProcessor == processors.STRIPE}
                <div class="right row" transition:slide|local>
                    <form>
                        <div class="row row collapse-to-col">
                            <h4 class="weight-400">Credit Card Number</h4>
                            <input
                                placeholder="1234 5678 9999 0000"
                                name="cc-num"
                                minlength="3"
                                required
                                aria-label="Credit Card Number"
                                class="solid-silver-border rounded-edge"
                            />
                        </div>
                        <div class="row row collapse-to-col">
                            <div class="half-row">
                                <h4 class="weight-400">Expiration Date</h4>
                                <input
                                    placeholder="MM/YY"
                                    type="text"
                                    name="exp"
                                    minlength="3"
                                    required
                                    aria-label="Expiration Date"
                                    class="solid-silver-border rounded-edge"
                                />
                            </div>
                            <div class="half-row">
                                <h4 class="weight-400">CVV</h4>
                                <input
                                    placeholder="CVV"
                                    type="text"
                                    name="cvv"
                                    minlength="3"
                                    required
                                    aria-label="CVV"
                                    class="solid-silver-border rounded-edge"
                                />
                            </div>
                        </div>
                    </form>
                </div>
            {/if}
        </div>
        <div class="row justify-content-center">
            <div class="row align-items-center justify-content-space-between">
                <h3 class="weight-400">{productDetails.name} {selectedPlan}</h3>
                <div class="weight-700 size-24px">{selectedCurrency}{selectedPrice}</div>
            </div>
            <HorizontalRule />
        </div>
        <Button buttonText="{'Complete Purchase'}" classNames="{'far-right-auto'}" on:click="{completePurchase}" />
    </div>
    <div class="card-content hidden" data-step="1" bind:this="{steps[1]}">
        <h3>Completing Purchase</h3>
        <HorizontalRule />
        <div class="spinner-container">
            <Spinner />
        </div>
    </div>
    <div class="card-content hidden error" data-step="2" bind:this="{steps[2]}">
        <h3>Oops! Something went wrong</h3>
        <HorizontalRule />
        <div class="row">
            <div class="warning-icon">
                <MaterialRoundedWarning fill="#e40d0d" />
            </div>
            <div class="row justify-content-center">
                <h2>Oops! Something went wrong</h2>
            </div>
        </div>
        <HorizontalRule />
        <div class="row align-items-center">
            <div>
                If the problem persists, please reach out to
                <a href="/">our support team</a>
                .
            </div>
            <Button buttonText="{'Try Again'}" classNames="{'far-right-auto'}" on:click="{() => navigateToIndex(0)}" />
        </div>
    </div>
</View>

<style>
    div.error h2 {
        margin-bottom: 48px;
        color: var(--textColorNormal);
    }

    div.row h4 {
        margin-bottom: 16px;
    }

    div[slot="leading"] {
        background-color: var(--primaryColor);
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        height: 100%;
        width: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0px;
        color: #fff;
    }
    .no-touch div[slot="leading"] i:hover {
        color: inherit;
    }

    div.spinner-container {
        margin: 96px auto;
    }

    div.error .warning-icon {
        width: 120px;
        height: auto;
        margin: 24px auto;
    }
</style>
